import { useMemo, type ReactNode } from 'react'
import { IoChevronBack } from 'react-icons/io5'
import { TbLayoutGrid, TbVocabulary } from 'react-icons/tb'
import { Link, useLocation } from 'react-router'

import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'
import { useMobileMode } from '@/Ship/Hooks/useMobileMode'

import { resolveDictionaryGroups, type DictionaryGroup, type DictionaryItem } from '../../Configs/dictionaries'
import { useListResources } from '../../Hooks'

import styles from './DictionariesShell.module.css'

/**
 * Сторінка огляду розділу — IndexPage контейнера. Шлях генерує ядро з назви секції
 * (`SettingSection` → `/setting`, однина), тоді як сторінки самих довідників — `/settings/...`
 * з `pageUrl` ресурсів.
 */
export const DICTIONARIES_INDEX_URL = '/setting'

const TITLE_ID = 'dictionaries-shell-title'

interface DictionariesShellProps {
    children: ReactNode
}

/** Активний довідник: його pageUrl є префіксом поточного шляху (картка `/settings/cargos/123` теж) */
const findCurrent = (groups: DictionaryGroup[], pathname: string): DictionaryItem | undefined =>
    groups
        .flatMap((group) => group.items)
        .find((item) => pathname === item.pageUrl || pathname.startsWith(item.pageUrl + '/'))

/**
 * Оболонка розділу «Довідники»: картка з шапкою, лівою навігацією по групах і робочою
 * частиною (огляд або таблиця довідника). На телефоні навігації немає: огляд — окремий
 * екран, у довіднику зверху кнопка «Довідники» назад.
 * Підключається як `layout` у ресурсах `Setting.List*` і напряму в IndexPage.
 */
export const DictionariesShell = ({ children }: DictionariesShellProps) => {
    const { pathname } = useLocation()
    const isMobile = useMobileMode(991)
    const resources = useListResources()
    const groups = useMemo(() => resolveDictionaryGroups(resources), [resources])
    const total = resources.length
    const current = findCurrent(groups, pathname)
    // Огляд визначаємо за шляхом, а не за «не знайшли довідник»: довідник поза списком
    // (напр. відфільтрований useListResources) не має маскуватися під огляд
    const isIndex = pathname.replace(/\/+$/, '') === DICTIONARIES_INDEX_URL

    const navItemClass = (isActive: boolean) =>
        `${cx(styles, 'navItem')} ${isActive ? cx(styles, 'navItemActive') : ''}`

    return (
        <div className={cx(styles, 'page')}>
            <section className={cx(styles, 'card')} aria-labelledby={TITLE_ID}>
                <header className={cx(styles, 'header')}>
                    <div className={cx(styles, 'headerAvatar')} aria-hidden="true">
                        <TbVocabulary />
                    </div>
                    <div className={cx(styles, 'headerMain')}>
                        <h1 id={TITLE_ID} className={cx(styles, 'title')}>
                            Довідники
                        </h1>
                        <div className={cx(styles, 'subtitle')}>
                            {total} {pluralizeUk(total, ['довідник', 'довідники', 'довідників'])}
                        </div>
                    </div>
                </header>

                <div className={cx(styles, 'body')}>
                    {isMobile ? (
                        !isIndex && (
                            <div className={cx(styles, 'mobileBar')}>
                                <Link to={DICTIONARIES_INDEX_URL} className={cx(styles, 'backLink')}>
                                    <IoChevronBack aria-hidden="true" />
                                    <span>Довідники</span>
                                </Link>
                                {current && <span className={cx(styles, 'mobileCurrent')}>{current.title}</span>}
                            </div>
                        )
                    ) : (
                        <nav className={cx(styles, 'nav')} aria-label="Перехід між довідниками">
                            <Link
                                to={DICTIONARIES_INDEX_URL}
                                className={navItemClass(isIndex)}
                                aria-current={isIndex ? 'page' : undefined}
                            >
                                <span className={cx(styles, 'navIcon')} aria-hidden="true">
                                    <TbLayoutGrid />
                                </span>
                                <span>Огляд</span>
                            </Link>

                            {groups.map((group) => {
                                const groupLabelId = `dictionaries-nav-group-${group.key}`
                                return (
                                    <div
                                        key={group.key}
                                        className={cx(styles, 'navGroup')}
                                        role="group"
                                        aria-labelledby={groupLabelId}
                                    >
                                        <div id={groupLabelId} className={cx(styles, 'navGroupLabel')}>
                                            {group.label}
                                        </div>
                                        {group.items.map((item) => {
                                            const isActive = item.resourceKey === current?.resourceKey
                                            const Icon = item.icon ?? TbLayoutGrid
                                            return (
                                                <Link
                                                    key={item.resourceKey}
                                                    to={item.pageUrl}
                                                    className={navItemClass(isActive)}
                                                    aria-current={isActive ? 'page' : undefined}
                                                >
                                                    <span className={cx(styles, 'navIcon')} aria-hidden="true">
                                                        <Icon />
                                                    </span>
                                                    <span className={cx(styles, 'navLabel')}>{item.title}</span>
                                                </Link>
                                            )
                                        })}
                                    </div>
                                )
                            })}
                        </nav>
                    )}

                    <div className={cx(styles, 'content')}>{children}</div>
                </div>
            </section>
        </div>
    )
}
